/**
 * Notion exports, read as a folder of Markdown.
 *
 * A Notion "Markdown & CSV" export is already close to a library: one `.md`
 * per page, sub-pages in folders, databases as `.csv`, images alongside. Three
 * things stand between that and notes you would want to keep:
 *
 *  - every file and folder carries Notion's page id — `Meeting notes
 *    1a2b…c0de.md` — which makes titles and folders unreadable;
 *  - links between pages are relative file paths, so they break the moment the
 *    files are no longer a folder on disk;
 *  - page properties are loose `Key: value` lines, invisible to anything that
 *    reads frontmatter.
 *
 * All three are fixed here, and the fixes are deliberately conservative: a link
 * that points at something not in the export is left exactly as written rather
 * than guessed at.
 */

/** Notion's id: 32 hex digits, or the dashed form older exports used. */
const ID = '[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const ID_SUFFIX = new RegExp(`[ _-](?:${ID})(?=$|\\.)`, 'i');

/** True when these archive paths look like they came out of Notion. */
export function looksLikeNotionExport(paths: readonly string[]): boolean {
  return paths.some((path) => path.split('/').some((part) => ID_SUFFIX.test(part)));
}

/** `Meeting notes 1a2b….md` → `Meeting notes`; leaves other names alone. */
export function cleanNotionName(segment: string): string {
  const dot = segment.lastIndexOf('.');
  const [stem, extension] = dot > 0 ? [segment.slice(0, dot), segment.slice(dot)] : [segment, ''];
  return stem.replace(ID_SUFFIX, '').trim() + extension;
}

/** The same, for a whole path. */
export function cleanNotionPath(path: string): string {
  return path.split('/').map(cleanNotionName).join('/');
}

/** A page's title from its file name, with the id and extension gone. */
export function notionTitleFromPath(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  return cleanNotionName(name).replace(/\.[^.]+$/, '') || 'Untitled';
}

/** Resolve a relative link found in the file at `from` against the archive. */
export function resolveNotionPath(from: string, href: string): string {
  let decoded = href;
  try {
    decoded = decodeURIComponent(href);
  } catch {
    // A href that is not valid percent-encoding is used as written.
  }
  if (decoded.startsWith('/')) return decoded.slice(1);
  const parts = from.split('/').slice(0, -1);
  for (const part of decoded.split('/')) {
    if (part === '.' || part === '') continue;
    if (part === '..') parts.pop();
    else parts.push(part);
  }
  return parts.join('/');
}

function isExternal(href: string): boolean {
  return /^[a-z][\w+.-]*:/i.test(href) || href.startsWith('#');
}

/** What the rest of the export offers a single page being converted. */
export interface NotionContext {
  /** Archive path → page title, for every `.md` and `.csv` in the export. */
  pages: ReadonlyMap<string, string>;
  /** Archive path → the bytes of an image or other attachment. */
  assets: ReadonlyMap<string, Blob>;
  /** Put an attachment somewhere and return the reference to use for it. */
  storeAsset?: (blob: Blob, path: string) => Promise<string>;
}

/**
 * Turn a page's links into links that still work once the files are gone.
 *
 * Page links become wiki links, so they behave like every other link in the
 * library — including to pages that were not exported, where the link at least
 * still says what it meant. Images are moved into the image store.
 */
export async function rewriteNotionLinks(
  markdown: string,
  path: string,
  context: NotionContext,
): Promise<string> {
  const pattern = /(!?)\[([^\]]*)\]\(([^)\s]+)\)/g;
  const replacements = new Map<string, string>();

  for (const match of markdown.matchAll(pattern)) {
    const [whole, bang, text, href] = match;
    if (replacements.has(whole) || isExternal(href!)) continue;
    const target = resolveNotionPath(path, href!);

    if (!bang) {
      const title = context.pages.get(target);
      if (title) {
        replacements.set(whole, text && text !== title ? `[[${title}|${text}]]` : `[[${title}]]`);
        continue;
      }
    }
    const blob = context.assets.get(target);
    if (blob && context.storeAsset) {
      replacements.set(whole, `${bang}[${text}](${await context.storeAsset(blob, target)})`);
    }
  }

  return replacements.size
    ? markdown.replace(pattern, (whole) => replacements.get(whole) ?? whole)
    : markdown;
}

function yamlValue(value: string): string {
  return /^[\w][\w .,/'-]*$/.test(value) ? value : `"${value.replace(/"/g, '\\"')}"`;
}

const PROPERTY_LINE = /^([A-Za-z][\w \-/]*):[ \t]+(.+)$/;

/** `Due date`, `Status`, `Multi-select` — a label, not the start of a sentence. */
function looksLikeKey(key: string): boolean {
  return key.length <= 32 && key.trim().split(/\s+/).length <= 3;
}

/**
 * Notion writes a page's properties as plain lines under the title. Lifted into
 * frontmatter they become properties markbird can show in a table and sort by;
 * left where they are they are just the first paragraph.
 *
 * A single `Word: something` line is left alone, because that is also what an
 * ordinary sentence looks like — turning someone's first paragraph into
 * metadata would be worse than missing one property.
 */
export function liftNotionProperties(markdown: string): string {
  const lines = markdown.split('\n');
  let index = 0;
  while (index < lines.length && lines[index]!.trim() === '') index++;
  if (!lines[index]?.startsWith('# ')) return markdown;
  const heading = index++;
  while (index < lines.length && lines[index]!.trim() === '') index++;

  const properties: [string, string][] = [];
  while (index < lines.length && lines[index]!.trim() !== '') {
    const match = PROPERTY_LINE.exec(lines[index]!);
    // Not a property block — a paragraph. Leave it.
    if (!match || !looksLikeKey(match[1]!)) return markdown;
    properties.push([match[1]!.trim(), match[2]!.trim()]);
    index++;
  }
  if (properties.length < 2) return markdown;
  while (index < lines.length && lines[index]!.trim() === '') index++;

  const seen = new Set<string>();
  const front: string[] = ['---'];
  for (const [rawKey, value] of properties) {
    const key = rawKey.toLowerCase().replace(/[ /]+/g, '-');
    if (seen.has(key)) continue;
    seen.add(key);
    front.push(
      key === 'tags' || key === 'keywords'
        ? `${key}: [${value
            .split(',')
            .map((tag) => yamlValue(tag.trim()))
            .filter(Boolean)
            .join(', ')}]`
        : `${key}: ${yamlValue(value)}`,
    );
  }
  front.push('---', '');

  return [...front, lines[heading], '', ...lines.slice(index)].join('\n').replace(/\n{3,}$/, '\n');
}

/** Split one CSV line, honouring quotes and doubled quotes inside them. */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === ',') {
      cells.push(cell);
      cell = '';
    } else cell += char;
  }
  cells.push(cell);
  return cells;
}

/** Rows of a CSV, keeping newlines that are inside quoted cells. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let line = '';
  let quotes = 0;
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    line = line ? `${line}\n${raw}` : raw;
    quotes += (raw.match(/"/g) ?? []).length;
    if (quotes % 2 === 0) {
      if (line.trim() !== '') rows.push(splitCsvLine(line));
      line = '';
    }
  }
  if (line.trim() !== '') rows.push(splitCsvLine(line));
  return rows;
}

/**
 * A Notion database as a Markdown table.
 *
 * The first column holds the row's page title, and where that page is also in
 * the export it becomes a wiki link — which is what makes an imported database
 * an index of real notes rather than a dead spreadsheet.
 */
export function notionCsvToMarkdown(
  csv: string,
  title: string,
  titles: ReadonlySet<string> = new Set(),
): string {
  const rows = parseCsv(csv);
  if (!rows.length) return `# ${title}\n`;
  const escape = (cell: string) => cell.replace(/\|/g, '\\|').replace(/\n+/g, ' ').trim();
  const [header, ...body] = rows;
  const width = header!.length;

  const line = (cells: string[]) =>
    `| ${Array.from({ length: width }, (_, i) => escape(cells[i] ?? '')).join(' | ')} |`;

  const out = [`# ${title}`, '', line(header!), `|${' --- |'.repeat(width)}`];
  for (const row of body) {
    const first = escape(row[0] ?? '');
    out.push(line([first && titles.has(first) ? `[[${first}]]` : first, ...row.slice(1)]));
  }
  return `${out.join('\n')}\n`;
}
